/** Hooks e rótulos das telas fiscais (Fase 7). A tela só pede: quem calcula e decide é a API. */
import {
  FISCAL_DOCUMENT_STATUS_LABEL,
  type FiscalAttentionKind,
  type FiscalDocumentStatus,
  type FiscalOperation,
  type FiscalOperationUseView,
  type FiscalReadiness,
  type FiscalSettings,
  type RuleDisplayStatus,
} from "@cafegestor/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { apiFetch, newIdempotencyKey } from "@/lib/api";
import { notifySuccess } from "@/lib/notice";

// chave das consultas fiscais e identificador do aparelho: desde a A0 no cliente compartilhado
import { FISCAL_KEY, deviceId } from "@cafegestor/client";

export { FISCAL_KEY, deviceId };

export function useFiscalQuery<T>(path: string, enabled = true) {
  return useQuery({
    queryKey: [...FISCAL_KEY, path],
    queryFn: () => apiFetch<T>(`/fiscal${path}`),
    enabled,
  });
}

export const useFiscalSettings = () => useFiscalQuery<FiscalSettings>("/configuracao");
export const useOperations = () => useFiscalQuery<FiscalOperation[]>("/operacoes");
export const useUses = () => useFiscalQuery<FiscalOperationUseView[]>("/usos");
export const useReadiness = (coverage = true) =>
  useFiscalQuery<FiscalReadiness>(`/prontidao?cobertura=${coverage ? "sim" : "nao"}`);

/**
 * Ação fiscal (POST com chave de idempotência por intenção; PUT sem). Depois do sucesso, a
 * chave é trocada e os dados fiscais são recarregados.
 */
export function useFiscalAction<TBody, TResult = unknown>(
  path: (body: TBody) => string,
  method: "POST" | "PUT" = "POST",
  onDone?: (r: TResult) => void,
  /** aviso depois que a API confirmou (texto fixo ou montado com a resposta) */
  success?: string | ((r: TResult) => string),
) {
  const queryClient = useQueryClient();
  const [key, setKey] = useState(newIdempotencyKey);
  return useMutation({
    mutationFn: (body: TBody) =>
      apiFetch<TResult>(`/fiscal${path(body)}`, {
        method,
        body,
        ...(method === "POST" ? { idempotencyKey: key } : {}),
      }),
    onSuccess: async (r) => {
      setKey(newIdempotencyKey());
      await queryClient.invalidateQueries({ queryKey: FISCAL_KEY });
      if (success) notifySuccess(typeof success === "string" ? success : success(r));
      onDone?.(r);
    },
  });
}

export const DOC_STATUS_TONE: Record<FiscalDocumentStatus, "neutral" | "ok" | "warn" | "error"> = {
  pendente: "warn",
  processando: "warn",
  incerta: "warn",
  contingencia: "warn",
  autorizada: "ok",
  rejeitada: "error",
  denegada: "error",
  cancelada: "neutral",
  abandonada: "neutral",
  inutilizada: "neutral",
};
export const docStatusLabel = (s: FiscalDocumentStatus) => FISCAL_DOCUMENT_STATUS_LABEL[s];

export const RULE_STATUS_LABEL: Record<RuleDisplayStatus, string> = {
  rascunho: "Rascunho",
  testada: "Testada",
  aprovada: "Aprovada",
  vigente: "Vigente",
  encerrada: "Encerrada",
  futura: "Aprovada (vigência futura)",
  arquivada: "Arquivada",
};
export const RULE_STATUS_TONE: Record<RuleDisplayStatus, "neutral" | "ok" | "warn" | "error"> = {
  rascunho: "warn",
  testada: "warn",
  aprovada: "ok",
  vigente: "ok",
  encerrada: "neutral",
  futura: "ok",
  arquivada: "neutral",
};

/** Formas de pagamento (tPag) mais usadas, para leitura humana. */
export const TPAG_LABEL: Record<string, string> = {
  "01": "Dinheiro",
  "02": "Cheque",
  "03": "Cartão de crédito",
  "04": "Cartão de débito",
  "05": "Crédito loja",
  "10": "Vale alimentação",
  "11": "Vale refeição",
  "12": "Vale presente",
  "13": "Vale combustível",
  "15": "Boleto",
  "16": "Depósito",
  "17": "PIX",
  "18": "Transferência",
  "19": "Fidelidade",
  "90": "Sem pagamento",
  "99": "Outros",
};

/** O que fazer em cada situação (sem reenvio às cegas). */
export const ATTENTION_GUIDANCE: Record<FiscalAttentionKind, string> = {
  rejeitada:
    "Corrija o que a SEFAZ apontou (cadastro do cliente, produto ou regra) e use “Recalcular e reenviar” — sai com o mesmo número. Se a venda não vai seguir, abandone a nota e estorne a venda.",
  denegada:
    "A SEFAZ denegou: o número fica usado e esta venda não pode seguir com nota. Estorne a venda (Vendas → Pedidos de venda).",
  incerta:
    "Sem resposta da SEFAZ. O sistema consulta a situação antes de qualquer novo envio; use “Consultar agora” se quiser antecipar. Não emita outra nota para a mesma venda.",
  falha:
    "O processamento parou (ex.: provedor recusou o acesso). Confira a configuração fiscal e use “Consultar agora”; o conteúdo e o número continuam os mesmos.",
  na_fila:
    "A nota está na fila há mais de 10 minutos: confira se o serviço de emissão (worker fiscal) está rodando.",
};
